import { useState, FunctionComponent } from 'react';
import { Typography, Box, Button } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

import { MonthInput } from 'components/common/MonthInput';
import { Label } from 'components/common/Label';
import useCollections, { CollectionState } from 'model/collection/CollectionStore';
import { validateEndDate } from 'model/collection/CollectionValidation';
import { nextMonth } from "model/collection/CollectionValidation"
import { getScheduleMessage } from 'utils/Common';

import { KeyboardArrowRight, KeyboardArrowLeft } from '@mui/icons-material';

export interface CardProps {
  back: () => void;
  backText: string;
  next: () => void;
  nextText: string;
  isNew: boolean;
  currentEndDate: string;
}

export const ScheduleCard: FunctionComponent<CardProps> = ({back, backText, next, nextText, isNew, currentEndDate}) => {
  // Store References
  const formData = useCollections((state: CollectionState) => state.collection);
  const updateCollection = useCollections((state: CollectionState) => state.updateCollection);
  // Violations
  const [violations, setViolations] = useState(new Map());

  const [scheduleMessage, setScheduleMessage] = useState(
    getScheduleMessage(formData.schedule, formData.dayOfTheMonth, formData.dayOfTheWeek)
  );

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    updateCollection(name, value);
    setScheduleMessage(getScheduleMessage(formData.schedule, formData.dayOfTheMonth, value));
  };

  const handleMonthChange = (event: any) => {
    const { name, value } = event.target;
    updateCollection(name, value);
    setScheduleMessage(getScheduleMessage(formData.schedule, value, formData.dayOfTheWeek));
  };

  const handleScheduleChange = (event: any) => {
    const { name, value } = event.target;
    if (value === "monthly") {
      updateCollection(name, value);
      handleMonthChange({ target: { name: "dayOfTheMonth", value: 1 } });
      updateCollection("dayOfTheWeek", null);
      setScheduleMessage(getScheduleMessage(value, 1, null));
    }
    if (value === "weekly") {
      updateCollection(name, value);
      handleMonthChange({ target: { name: "dayOfTheMonth", value: null } });
      updateCollection("dayOfTheWeek", 1);
      setScheduleMessage(getScheduleMessage(value, null, 1));
    }
  };

  const handleDateTimeChange = (name: string, date: dayjs.Dayjs): any => {
    updateCollection(name, date);
  };

  const handleNext = () => {
      if (violations.size > 0) {
        return;
      }
      next();
  }

  return (<>
    <Typography variant='h5'>Schedule</Typography>
    <Typography variant='body1' color="secondary">
      Select how often to send notifications. The supported schedules are 'weekly' or 'monthly'.
    </Typography>
    <br/>
    <Grid container spacing={2}>
      <Grid size={{ xs: 6 }}>

        <InputLabel sx={{ fontSize: 15, fontWeight: 600 }}>
          Schedule
        </InputLabel>
        <Select required
          sx={{ mx: 0, mt: "2px", mb: "14px" }}
          fullWidth
          variant="outlined"
          value={formData.schedule}
          name="schedule"
          onChange={handleScheduleChange}>
          <MenuItem value={"monthly"} selected>Monthly</MenuItem>
          <MenuItem value={"weekly"}>Weekly</MenuItem>
        </Select>
      </Grid>

      <Grid size={{ xs: 6 }}>
        {formData.schedule === "monthly" ?
          <>
            <MonthInput
              name="dayOfTheMonth"
              value={formData.dayOfTheMonth}
              error=""
              label="Day of the Month"
              onChange={handleMonthChange} />
          </> : <>
            <InputLabel sx={{ fontSize: 15, fontWeight: 600 }}>
              Day of the Week
            </InputLabel>
            <Select required
              sx={{ mx: 0, mt: "2px", mb: "14px" }}
              fullWidth
              variant="outlined"
              value={formData.dayOfTheWeek}
              name="dayOfTheWeek"
              onChange={handleChange}>
              <MenuItem value={1}>Monday</MenuItem>
              <MenuItem value={2}>Tuesday</MenuItem>
              <MenuItem value={3}>Wednesday</MenuItem>
              <MenuItem value={4}>Thursday</MenuItem>
              <MenuItem value={5}>Friday</MenuItem>
              <MenuItem value={6}>Saturday</MenuItem>
              <MenuItem value={7}>Sunday</MenuItem>
            </Select>
          </>
        }
      </Grid>
    </Grid>

    <Label label={isNew?`End Date (up to ${dayjs().add(1, 'year').format("MMM Do, YYYY")})`:
                  formData.status === "expired"?`End Date (up to ${dayjs(currentEndDate).add(1, 'year').format("MMM Do, YYYY")})`:
                   `End Date (up to ${dayjs(formData.createDateTime).add(1, 'year').format("MMM Do, YYYY")})`
                 }
           infoMessage="For longer than one year, please use contact us form" />
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker 
        sx={{ width: "100%", mt: "2px", mb: violations?.get("endDate")?"8px":"31px" }}
        slotProps={{
          textField: {
            required: true,
            helperText: violations?.get("endDate"),
            error: violations?.has("endDate")
          },
        }}
        disablePast={true}
        minDate={nextMonth()}
        maxDate={formData.status === "expired"?
          dayjs(currentEndDate).add(1, 'year'):
          dayjs(formData.createDateTime).add(1, 'year')
        }
        value={dayjs(formData.endDate)}
        onChange={(d) => {
          handleDateTimeChange("endDate", d);
          let dateViolation = validateEndDate(isNew, d, formData.status, formData.createDateTime, currentEndDate);
          if (dateViolation) {
            violations.set(dateViolation[0], dateViolation[1]);
          } else {
            setViolations(new Map())
          }
        }} />
    </LocalizationProvider>

    <Grid container alignItems="center">
      <Grid style={{ width: 34, display: 'flex', alignItems: 'center' }}>
        <InfoIcon color="secondary" />
      </Grid>
      <Grid size="grow">
        <Typography variant='body2' color="secondary">
          {scheduleMessage}
        </Typography>
      </Grid>
    </Grid>

    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
      <Button color="inherit" onClick={back} sx={{ mr: 1 }}>
        <KeyboardArrowLeft/>{backText}
      </Button>
      <Box sx={{ flex: '1 1 auto' }} />
      <Button color="inherit" onClick={handleNext}>
        {nextText}<KeyboardArrowRight/>
      </Button>
    </Box>
  </>);
}
export default ScheduleCard;